import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("vcs_dpdp_consent");
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem("vcs_dpdp_consent", JSON.stringify({ choice: "all", date: new Date().toISOString() }));
    setVisible(false);
  };

  const handleNecessaryOnly = () => {
    localStorage.setItem("vcs_dpdp_consent", JSON.stringify({ choice: "necessary", date: new Date().toISOString() }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.98 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-50 bg-[#2A0E20]/95 backdrop-blur-xl border border-[#D4AF37]/30 text-white p-5 rounded-2xl shadow-2xl"
          data-testid="dpdp-cookie-consent-banner"
          role="region"
          aria-label="Privacy and Cookie Consent"
        >
          <div className="flex items-start justify-between gap-3 mb-2.5">
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <ShieldCheck size={18} />
              <span className="font-heading font-bold text-sm tracking-wide">Privacy & DPDP Consent</span>
            </div>
            <button
              onClick={handleNecessaryOnly}
              className="text-white/50 hover:text-white transition-colors p-1"
              aria-label="Dismiss banner"
            >
              <X size={16} />
            </button>
          </div>

          <p className="text-xs text-white/75 leading-relaxed mb-4">
            We process technical and preference data in accordance with India&apos;s <strong>Digital Personal Data Protection Act, 2023 (DPDP)</strong> to ensure seamless site operations and recruitment inquiry handling.
          </p>

          <div className="flex flex-wrap items-center gap-2.5">
            <button
              onClick={handleAcceptAll}
              className="px-4 py-2 bg-[#D4AF37] hover:bg-white text-[#4A1136] text-xs font-bold rounded-xl transition-all duration-200 active:scale-95 shadow-md"
              data-testid="consent-accept-all-btn"
            >
              Accept All
            </button>
            <button
              onClick={handleNecessaryOnly}
              className="px-3.5 py-2 bg-white/10 hover:bg-white/15 text-white/90 text-xs font-semibold rounded-xl border border-white/15 transition-all duration-200 active:scale-95"
              data-testid="consent-necessary-btn"
            >
              Essential Only
            </button>
            <Link
              to="/privacy"
              className="text-xs text-[#D4AF37] hover:underline transition-all ml-auto self-center"
              data-testid="consent-notice-link"
            >
              DPDP Notice →
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
